import { Injectable, computed, inject, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';

import { BackendService } from './backend.service';
import { UsageSummary, UsageLedger } from '../models/usage.model';

/** How a usage window relates to its limit. Drives the avatar ring colour, the
 *  usage-meter bar colours, and whether the chat shows a warning banner. */
export type UsageLevel = 'ok' | 'approaching' | 'reached';

/** Percent-of-limit at which a window flips to "approaching" (amber). Mirrors the
 *  usage-meter's long-standing 80% warn threshold so ring/meter/banner agree. */
const APPROACHING_PCT = 80;

/** The ledger Hub chat consumes. Quotas are enforced PER-LEDGER on
 *  {cost_center}#{username}, so Hub's composer must gate on THIS ledger's
 *  usage, not the combined total across all ledgers (which would falsely pause
 *  Hub when Studio/BUILDER usage dominates). */
const HUB_LEDGER = 'END_USER';

/**
 * Owns the caller's token-usage state for the "my usage" widget, the avatar
 * usage ring, and the chat usage-warning banner.
 *
 * Reads GET /agenthome/me/usage (the user's own daily/monthly tokens + limit).
 * Components render the derived signals below; the chat component calls refresh()
 * on boot and after each completed turn. Fails soft — a read error never blocks
 * chat, the widgets just show "unavailable".
 */
@Injectable({ providedIn: 'root' })
export class UsageService {
  private readonly http = inject(HttpClient);
  private readonly backend = inject(BackendService);

  readonly usage = signal<UsageSummary | null>(null);
  readonly loading = signal(false);
  readonly errored = signal(false);

  /** Set when the user dismisses the "approaching" banner — sticky for the tab
   *  session so it doesn't reappear on every refresh. A "reached" banner ignores
   *  this (it's not dismissable). Cleared on reset() (logout). */
  readonly warningDismissed = signal(false);

  // ── Derived usage state (shared by ring / meter / banner) ──────────
  readonly unlimited = computed(() => this.usage()?.unlimited ?? false);

  /** Clamped 0–100 fill percentages for progress bars / the ring arc. */
  readonly dailyPct = computed(() => this.clampPct(this.usage()?.daily.pct_used));
  readonly monthlyPct = computed(() => this.clampPct(this.usage()?.monthly.pct_used));

  readonly dailyLevel = computed<UsageLevel>(() =>
    this.levelFor(this.usage()?.daily.pct_used, this.unlimited()));
  readonly monthlyLevel = computed<UsageLevel>(() =>
    this.levelFor(this.usage()?.monthly.pct_used, this.unlimited()));

  /** Worst of the two windows — informational total only. Does NOT gate Hub's
   *  composer (that uses endUserLevel below); kept for the meter widget. */
  readonly level = computed<UsageLevel>(() =>
    this.worst(this.dailyLevel(), this.monthlyLevel()));

  /** Which window is closest to its limit — the (combined) widget reports on this. */
  readonly worstScope = computed<'daily' | 'monthly'>(() => {
    const u = this.usage();
    if (!u) return 'monthly';
    return (u.daily.pct_used ?? 0) >= (u.monthly.pct_used ?? 0) ? 'daily' : 'monthly';
  });

  // ── END_USER (Hub) ledger state — what the composer is GATED on ────
  // Quotas are enforced per-ledger; Hub chat only ever spends END_USER, so its
  // pause must reflect the END_USER ledger's own daily/monthly pct — NOT the
  // combined totals (where BUILDER/Studio usage could falsely pause Hub).

  /** The END_USER ledger slice, or null when the API omits it (unlimited /
   *  no Hub spend yet). Safe optional chaining — `ledgers` is optional. */
  readonly endUserLedger = computed<UsageLedger | null>(() =>
    this.usage()?.ledgers?.find(l => l.cost_center === HUB_LEDGER) ?? null);

  /** Level of the END_USER ledger's daily window. 'ok' when the ledger is absent
   *  (no per-ledger cap surfaced) or the ledger itself is unlimited. */
  readonly endUserDailyLevel = computed<UsageLevel>(() => {
    const lg = this.endUserLedger();
    if (!lg) return 'ok';
    return this.levelFor(lg.daily?.pct_used, lg.unlimited);
  });

  readonly endUserMonthlyLevel = computed<UsageLevel>(() => {
    const lg = this.endUserLedger();
    if (!lg) return 'ok';
    return this.levelFor(lg.monthly?.pct_used, lg.unlimited);
  });

  /** Worst of the END_USER ledger's two windows — what gates/labels the composer. */
  readonly endUserLevel = computed<UsageLevel>(() =>
    this.worst(this.endUserDailyLevel(), this.endUserMonthlyLevel()));

  /** Which END_USER window is closest to its limit — drives the pause tooltip/banner. */
  readonly endUserWorstScope = computed<'daily' | 'monthly'>(() => {
    const lg = this.endUserLedger();
    if (!lg) return 'monthly';
    return (lg.daily?.pct_used ?? 0) >= (lg.monthly?.pct_used ?? 0) ? 'daily' : 'monthly';
  });

  private clampPct(pct: number | undefined): number {
    return Math.max(0, Math.min(100, Math.round(pct ?? 0)));
  }

  /** Worse of two levels (reached > approaching > ok). */
  private worst(a: UsageLevel, b: UsageLevel): UsageLevel {
    const rank = { ok: 0, approaching: 1, reached: 2 } as const;
    return rank[a] >= rank[b] ? a : b;
  }

  private levelFor(pct: number | undefined, unlimited: boolean): UsageLevel {
    if (unlimited || pct == null) return 'ok';
    if (pct >= 100) return 'reached';
    if (pct >= APPROACHING_PCT) return 'approaching';
    return 'ok';
  }

  /** Hide the "approaching" banner for the rest of the session. */
  dismissWarning(): void {
    this.warningDismissed.set(true);
  }

  private inFlight = false;

  /** Single-flight refresh; only shows the loading state on the first fetch. */
  refresh(): void {
    if (this.inFlight) return;
    this.inFlight = true;
    if (this.usage() === null) this.loading.set(true);

    const url = this.backend.createAgentsUrl('agenthome/me/usage');
    this.http.get<{ success: boolean; body: UsageSummary | null }>(url).subscribe({
      next: res => {
        this.usage.set(res?.body ?? null);
        this.errored.set(false);
        this.loading.set(false);
        this.inFlight = false;
      },
      error: () => {
        this.errored.set(true);
        this.loading.set(false);
        this.inFlight = false;
      },
    });
  }

  /** Clear on logout so usage never leaks to the next user in the same tab. */
  reset(): void {
    this.usage.set(null);
    this.errored.set(false);
    this.loading.set(false);
    this.warningDismissed.set(false);
  }
}
